import React from "react";
import { ProgressBar, Table } from "react-bootstrap";
import { ActionMenu } from "../../../../components/Ads/ActionMenu/ActionMenu";
import type { AdCampaign } from "../../../../api/ads";
import { AdsCampaignStatus } from "../../../../api/ads";

import "./AdCampaignTable.css";

export interface AdCampaignTableProps {
  campaigns: AdCampaign[];
  onView: (campaign: AdCampaign) => void;
  onEdit: (campaign: AdCampaign) => void;
  onStatusChange: (id: string, status: AdsCampaignStatus) => void;
}

const statusBadgeClass = (status: AdsCampaignStatus): string => {
  switch (status) {
    case AdsCampaignStatus.ACTIVE:
      return "ads-status-badge ads-status-badge--active";
    case AdsCampaignStatus.EXPIRED:
      return "ads-status-badge ads-status-badge--expired";
    case AdsCampaignStatus.DRAFT:
      return "ads-status-badge ads-status-badge--draft";
    case AdsCampaignStatus.COMPLETED:
      return "ads-status-badge ads-status-badge--completed";
    case AdsCampaignStatus.PAUSED:
      return "ads-status-badge ads-status-badge--paused";
    case AdsCampaignStatus.ARCHIVED:
      return "ads-status-badge ads-status-badge--archived";
    default:
      return "ads-status-badge ads-status-badge--archived";
  }
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const statusLabel = (status: AdsCampaignStatus): string =>
  status.charAt(0) + status.slice(1).toLowerCase();

export const AdCampaignTable: React.FC<AdCampaignTableProps> = ({
  campaigns,
  onView,
  onEdit,
  onStatusChange,
}) => {
  return (
    <div className="ads-table-wrapper">
      <Table responsive className="ads-table">
        <thead>
          <tr>
            <th>
              Campaign<br />Name
            </th>
            <th>
              Advertiser<br />Name
            </th>
            <th>Placement</th>
            <th>
              Pricing<br />Model
            </th>
            <th>Budget/Spend</th>
            <th>Validity</th>
            <th>Status</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const pct =
              c.budget > 0
                ? Math.max(0, Math.min(100, Math.round((c.spend / c.budget) * 100)))
                : 0;
            const isExpired = c.status === AdsCampaignStatus.EXPIRED;
            return (
              <tr key={c.id}>
                <td>
                  <button
                    type="button"
                    className="ads-linklike ads-linklike--button"
                    onClick={() => onView(c)}
                  >
                    {c.name}
                  </button>
                </td>
                <td>{c.advertiserName}</td>
                <td>{c.placement}</td>
                <td>{c.pricingModel}</td>
                <td>
                  <div className="ads-budget-cell">
                    <div className="ads-budget-cell__values">
                      <span>{formatMoney(c.spend)}</span>
                      <span>{formatMoney(c.budget)}</span>
                    </div>
                    <ProgressBar now={pct} className="ads-budget-cell__bar" />
                  </div>
                </td>
                <td>
                  <span className={`ads-validity${isExpired ? " ads-validity--muted" : ""}`}>
                    {formatDate(c.startAt)} -{"\n"}{formatDate(c.endAt)}
                  </span>
                </td>
                <td>
                  <span className={statusBadgeClass(c.status)}>
                    {statusLabel(c.status)}
                  </span>
                </td>
                <td className="text-center">
                  <ActionMenu
                    items={[
                      {
                        key: "view",
                        label: "View",
                        iconClassName: "ph-eye",
                        anyOfPermissions: ["ads.view", "ads.admin", "ads.manage"],
                        hideWhenDisabled: true,
                        onSelect: () => onView(c),
                      },
                      {
                        key: "edit",
                        label: "Edit",
                        iconClassName: "ph-pencil",
                        anyOfPermissions: ["ads.admin", "ads.manage"],
                        hideWhenDisabled: true,
                        onSelect: () => onEdit(c),
                      },
                      ...[
                        {
                          key: "set-draft",
                          label: "Draft",
                          iconClassName: "ph-pencil-line",
                          status: AdsCampaignStatus.DRAFT,
                          onSelect: () => onStatusChange(c.id, AdsCampaignStatus.DRAFT),
                        },
                        {
                          key: "activate",
                          label: "Activate",
                          iconClassName: "ph-play-circle",
                          status: AdsCampaignStatus.ACTIVE,
                          onSelect: () => onStatusChange(c.id, AdsCampaignStatus.ACTIVE),
                        },
                        {
                          key: "pause",
                          label: "Pause",
                          iconClassName: "ph-pause-circle",
                          status: AdsCampaignStatus.PAUSED,
                          onSelect: () => onStatusChange(c.id, AdsCampaignStatus.PAUSED),
                        },
                        {
                          key: "archive",
                          label: "Archive",
                          iconClassName: "ph-archive",
                          status: AdsCampaignStatus.ARCHIVED,
                          onSelect: () => onStatusChange(c.id, AdsCampaignStatus.ARCHIVED),
                        },
                        {
                          key: "complete",
                          label: "Mark as Complete",
                          iconClassName: "ph-check-circle",
                          status: AdsCampaignStatus.COMPLETED,
                          onSelect: () => onStatusChange(c.id, AdsCampaignStatus.COMPLETED),
                        },
                      ]
                        .filter((item) => item.status !== c.status)
                        .map(({ status: _status, ...item }) => ({
                          ...item,
                          anyOfPermissions: ["ads.admin", "ads.manage"],
                          hideWhenDisabled: true,
                        })),
                    ]}
                    ariaLabel={`Campaign actions for ${c.name}`}
                    size="sm"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
